import { AlertCircle, Clock } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { dueChipLabel, groupTasks, type TaskGroupKey } from "@/lib/dashboard/groupTasks";
import { CrmTaskStatus, type CrmTask } from "@/types/crm";

const GROUP_COLORS: Record<TaskGroupKey, string> = {
  overdue: "text-destructive",
  today: "text-amber-600 dark:text-amber-400",
  later: "text-muted-foreground",
};

export interface UpcomingTasksPanelProps {
  tasks: CrmTask[];
  onComplete: (task: CrmTask) => void;
  onOpen?: (task: CrmTask) => void;
  now?: Date;
}

export function UpcomingTasksPanel({ tasks, onComplete, onOpen, now }: UpcomingTasksPanelProps) {
  // The dashboard keeps a just-completed task in the list until remount, so
  // completed rows are rendered (checked, struck through) rather than dropped.
  const groups = groupTasks(tasks, now, { keepCompleted: true });

  if (groups.length === 0) {
    return (
      <div className="border-t px-4 py-3 text-sm text-muted-foreground">No upcoming tasks</div>
    );
  }

  return (
    <div>
      {groups.map((group) => (
        <div key={group.key}>
          <div
            data-testid="task-group-label"
            className={`border-t px-4 py-1.5 font-mono text-[10px] uppercase tracking-[0.14em] ${GROUP_COLORS[group.key]}`}
          >
            {group.label} · {group.tasks.length}
          </div>

          {group.tasks.map((task) => {
            const chip = dueChipLabel(task, now);
            const isCompleted = task.status === CrmTaskStatus.Completed;
            return (
              <div
                key={task.id}
                role="button"
                tabIndex={0}
                aria-label={`Open task: ${task.title}`}
                className="flex cursor-pointer items-center gap-3 border-t px-4 py-2.5 transition-colors hover:bg-muted/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                onClick={() => onOpen?.(task)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    onOpen?.(task);
                  }
                }}
              >
                <span onClick={(e) => e.stopPropagation()}>
                  <Checkbox
                    checked={isCompleted}
                    disabled={isCompleted}
                    onCheckedChange={() => onComplete(task)}
                    aria-label={`Complete ${task.title}`}
                  />
                </span>

                <div className="min-w-0 flex-1">
                  <p
                    className={`line-clamp-2 text-[13.5px] font-medium ${
                      isCompleted ? "text-muted-foreground line-through" : ""
                    }`}
                  >
                    {task.title}
                  </p>
                  {isCompleted ? (
                    <p className="line-clamp-2 text-xs text-muted-foreground">completed just now</p>
                  ) : (
                    task.projectName && (
                      <p className="line-clamp-2 text-xs text-muted-foreground">{task.projectName}</p>
                    )
                  )}
                </div>

                {isCompleted ? (
                  <span className="shrink-0 text-xs text-muted-foreground">Done</span>
                ) : (
                  <div
                    className={`flex shrink-0 items-center gap-1 text-xs ${
                      group.key === "overdue"
                        ? "font-medium text-destructive"
                        : group.key === "today"
                          ? "font-medium text-amber-600 dark:text-amber-400"
                          : "text-muted-foreground"
                    }`}
                  >
                    {group.key === "overdue" ? (
                      <AlertCircle className="h-3.5 w-3.5" />
                    ) : (
                      <Clock className="h-3.5 w-3.5" />
                    )}
                    {chip}
                  </div>
                )}

                {!isCompleted && task.status === CrmTaskStatus.InProgress && (
                  <Badge variant="secondary" className="shrink-0 text-xs">
                    In Progress
                  </Badge>
                )}
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );
}
